import { Button, Form, Input, Select, Tooltip } from "antd";
import TextArea from "antd/lib/input/TextArea";
import FeatureSpecSectionDropdown from "components/feature-spec-section-dropdown";
import FileViewerModal from "components/file-viewer-modal/file-viewer-modal";
import { ProjectContext } from "context/ProjectProvider";
import { Priorities } from "entity-app/constants";
import { TFetureDetailUpdateAction } from "entity-app/models";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useShowDesignAndSpec } from "hooks/common";
import { SubmittalTypeType } from "models/submittal-log";
import { useContext, useEffect, useState } from "react";
import { QUERY_SUBMITTAL_TYPES } from "services/graphQL/queries";
import { updatePropertyAtPath } from "utils/utils";
import { useSubmmittialDetails } from "../hook/submittal-details-hook";

export default function SubmittalGeneralInfo(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  submittal: any;
  editableFields: any[];
  isRevisionMode: boolean;
  updateFeatureData: {
    localFeatureInstanceData: any;
    setLocalFeatureInstanceData: Function;
  };
}) {
  const {
    featureInstanceData,
    actions,
    submittal,
    editableFields,
    isRevisionMode,
    updateFeatureData: { localFeatureInstanceData, setLocalFeatureInstanceData }
  } = props;
  const [form] = Form.useForm();

  const { Option } = Select;
  const projectContext = useContext(ProjectContext);
  const columnHeaders = projectContext?.columnHeaders;
  const submittalHeaderMap = columnHeaders?.submittalHeaderMap || {};

  const { isDesignTabEnabled } = useShowDesignAndSpec();
  const { data: dataSubmittalTypes } = useCIQQuery(QUERY_SUBMITTAL_TYPES);
  const [showSpecSectionFileModal, setShowSpecSectionFileModal] =
    useState(false);
  const {
    isSubmittalInDoneState,
    cannotRespondToWorkflow,
    isIntegrationMode,
    generalInfoDisabledTooltipMsg,
    submittalParticipants,
    permission: { isLoggedInUserSC }
  } = useSubmmittialDetails({
    featureInstanceData: localFeatureInstanceData,
    isRevisionMode
  });

  const specFileModalHeader = () => {
    return (
      <div className="w-full grid grid-cols-3 gap-x-6">
        <div className="col-span-2 truncate">
          {submittal?.spec_no && <span>{submittal?.spec_no} -</span>}{" "}
          {submittal?.spec_name || ""}
        </div>
      </div>
    );
  };

  const specSectionLabelWithViewer = (
    <div className="w-full flex items-center justify-between">
      <div>{submittalHeaderMap?.spec_no}</div>
      {isDesignTabEnabled && submittal?.snippet_file_key ? (
        <Button
          type="link"
          size="small"
          className="text-xs uppercase workflow-document-upload h-[17px] mb-1"
          onClick={() => {
            setShowSpecSectionFileModal(true);
          }}
          data-testid="sgi-button-view-spec-section-file"
        >
          view spec section file
        </Button>
      ) : null}
    </div>
  );
  useEffect(() => {
    form.setFieldsValue({
      priority: submittal?.priority,
      raw_type: submittal?.raw_type
    });
  }, [form, submittal]);

  const readOnlyGeneralInfoCard = () => {
    const specSection = localFeatureInstanceData?.feature_instance_spec_sections
      ?.length
      ? localFeatureInstanceData?.feature_instance_spec_sections[0]
          ?.spec_section
      : null;

    const specSectionTitle = specSection?.section_name
      ? `${specSection?.section_number || ""} - ${
          specSection?.section_name || ""
        }`
      : specSection?.section_number || "";

    return (
      <div
        className="grow min-h-0 h-[400px] overflow-auto"
        data-testid="sgi-readonly-root"
      >
        <Form layout="vertical" data-testid="sgi-readonly-form">
          <div className="w-full flex">
            <Form.Item label="State" className="w-1/3">
              <span className="font-bold" data-testid="sgi-state-value">
                {submittal?.raw_status || ""}
              </span>
            </Form.Item>
            <Form.Item label="Ball In Court" className="w-2/3">
              {submittalParticipants?.assigneeUnregistered() && (
                <div>
                  <span className="font-bold">
                    {submittalParticipants?.assigneeUnregistered()?.fullName}
                  </span>
                  {submittalParticipants?.assigneeUnregistered()?.company
                    ?.name !== "" && (
                    <span className="ml-1 text-xs">
                      (
                      {
                        submittalParticipants?.assigneeUnregistered()?.company
                          ?.name
                      }
                      )
                    </span>
                  )}
                </div>
              )}
              {submittalParticipants?.assignee && (
                <div>
                  <span className="font-bold">
                    {submittalParticipants?.assignee?.user?.first_name}{" "}
                    {submittalParticipants?.assignee?.user?.last_name}
                  </span>
                  {submittalParticipants?.assigneeCompany &&
                    submittalParticipants?.assigneeCompany !== "" && (
                      <span className="ml-1 text-xs">
                        ({submittalParticipants?.assigneeCompany})
                      </span>
                    )}
                </div>
              )}
            </Form.Item>
          </div>
          <Form.Item
            label={submittalHeaderMap?.submittal_number || "Submittal Number"}
          >
            <span>{localFeatureInstanceData?.number}</span>
          </Form.Item>
          <Form.Item label={specSectionLabelWithViewer}>
            <span>{specSectionTitle}</span>
          </Form.Item>
          <Form.Item label="TYPE">
            <span>{submittal?.raw_type}</span>
          </Form.Item>
          <Form.Item label={submittalHeaderMap?.priority || "Priority"}>
            <span>{submittal?.priority}</span>
          </Form.Item>
          <Form.Item label="Description">
            <span>{localFeatureInstanceData?.description}</span>
          </Form.Item>
        </Form>
      </div>
    );
  };

  if (isLoggedInUserSC) return readOnlyGeneralInfoCard();

  const checkValuesUpdated = (key: string, updatedValue: string) => {
    return (featureInstanceData[key] ?? "") !== updatedValue;
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="grow min-h-0 h-[396px] overflow-auto"
      data-testid="sgi-form"
    >
      <div className="w-full flex">
        <Form.Item label={submittalHeaderMap?.state} className="w-1/3">
          <span data-testid="sgi-state-value-edit">
            {submittal?.raw_status || ""}
          </span>
        </Form.Item>
        <Form.Item label={submittalHeaderMap?.assignee} className="w-2/3">
          {submittalParticipants?.assigneeUnregistered() && (
            <div>
              <span className="font-bold">
                {submittalParticipants?.assigneeUnregistered()?.fullName}
              </span>
              {submittalParticipants?.assigneeUnregistered()?.company?.name !==
                "" && (
                <span className="ml-1 text-xs">
                  (
                  {submittalParticipants?.assigneeUnregistered()?.company?.name}
                  )
                </span>
              )}
            </div>
          )}
          {submittalParticipants?.assignee && (
            <span>{`${
              submittalParticipants?.assignee?.user?.first_name ?? ""
            } ${submittalParticipants?.assignee?.user?.last_name ?? ""} ${
              submittalParticipants?.assigneeCompany
                ? `(${submittalParticipants?.assigneeCompany})`
                : ""
            }`}</span>
          )}
        </Form.Item>
      </div>
      <Form.Item label={submittalHeaderMap?.submittal_number}>
        <Input
          value={localFeatureInstanceData?.number}
          onChange={(event: any) => {
            setLocalFeatureInstanceData(
              updatePropertyAtPath(
                localFeatureInstanceData,
                "number",
                event.target.value
              )
            );
          }}
          onBlur={async (event: any) => {
            if (!checkValuesUpdated("number", event?.target?.value?.trim()))
              return;
            actions.updateFeatureInstanceFields({
              number: event.target.value?.trim()
            });
            setLocalFeatureInstanceData(
              updatePropertyAtPath(
                localFeatureInstanceData,
                "number",
                event.target.value
              )
            );
          }}
          disabled={
            isRevisionMode ||
            isIntegrationMode ||
            isSubmittalInDoneState ||
            cannotRespondToWorkflow ||
            !editableFields.includes("submittal_number")
          }
          data-testid="sgi-input-number"
        />
      </Form.Item>
      <Tooltip title={generalInfoDisabledTooltipMsg} placement="rightBottom">
        <Form.Item label={specSectionLabelWithViewer}>
          <FeatureSpecSectionDropdown
            disabled={
              isRevisionMode ||
              isIntegrationMode ||
              isSubmittalInDoneState ||
              cannotRespondToWorkflow ||
              !editableFields.includes("spec_section")
            }
            mode="single"
            selectedIds={localFeatureInstanceData?.feature_instance_spec_sections?.map(
              (specSection: any) => {
                return specSection?.spec_section?.id;
              }
            )}
            onBlur={(ids) => {
              const specSection = featureInstanceData
                ?.feature_instance_spec_sections?.length
                ? featureInstanceData?.feature_instance_spec_sections[0]
                    ?.spec_section
                : {};

              if (ids.includes(specSection?.id)) return;
              actions.updateFeatureInstanceSpecSections({
                ids,
                bidscope: false
              });

              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_instance_spec_sections",
                  [
                    {
                      bid_scope: false,
                      spec_section: {
                        ...specSection,
                        id: ids[0]
                      }
                    }
                  ]
                )
              );
            }}
            data-testid="sgi-dropdown-spec-section"
          />
        </Form.Item>
      </Tooltip>
      <Tooltip title={generalInfoDisabledTooltipMsg} placement="rightBottom">
        <Form.Item label={submittalHeaderMap?.type} name="raw_type">
          <Select
            allowClear
            onChange={(id: string) => {
              if (
                submittal?.type === Number(id) ||
                (submittal?.type === null && id === undefined)
              )
                return;

              const typeObj = dataSubmittalTypes?.configurations_value?.find(
                (t: any) => {
                  return t.id === Number(id);
                }
              );
              if (typeObj) {
                actions.updateSubmittalFields({
                  type: typeObj.id,
                  raw_type: typeObj.value
                });
              } else {
                actions.updateSubmittalFields({
                  type: null,
                  raw_type: ""
                });
              }

              const featureSubmittals =
                localFeatureInstanceData?.feature_submittals?.length &&
                localFeatureInstanceData?.feature_submittals[0];

              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_submittals",
                  [
                    {
                      ...featureSubmittals,
                      type: id || null,
                      raw_type: typeObj?.value || ""
                    }
                  ]
                )
              );
            }}
            disabled={
              isRevisionMode ||
              isIntegrationMode ||
              isSubmittalInDoneState ||
              cannotRespondToWorkflow ||
              !editableFields.includes("type")
            }
            data-testid="sgi-select-type"
          >
            {dataSubmittalTypes?.configurations_value?.map(
              (item: SubmittalTypeType) => (
                <Option key={item.id} label={item.value}>
                  {item.value}
                </Option>
              )
            )}
          </Select>
        </Form.Item>
      </Tooltip>
      <Tooltip title={generalInfoDisabledTooltipMsg} placement="rightBottom">
        <Form.Item
          name="priority"
          label={submittalHeaderMap?.priority || "Priority"}
        >
          <Select
            placeholder={`Select a ${
              submittalHeaderMap?.priority || "Priority"
            }`}
            allowClear
            onChange={(label) => {
              if (submittal?.priority === label) return;
              const featureSubmittals =
                localFeatureInstanceData?.feature_submittals?.length &&
                localFeatureInstanceData?.feature_submittals[0];

              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_submittals",
                  [
                    {
                      ...featureSubmittals,
                      priority: label || null
                    }
                  ]
                )
              );
              if (label === undefined) {
                actions.updateSubmittalFields({
                  priority: null
                });
              } else {
                actions.updateSubmittalFields({
                  priority: label
                });
              }
            }}
            disabled={
              isRevisionMode ||
              cannotRespondToWorkflow ||
              isIntegrationMode ||
              !editableFields.includes("priority")
            }
            data-testid="sgi-select-priority"
          >
            {Priorities?.map((priorityItem: any) => (
              <Option key={priorityItem.value} label={priorityItem.label}>
                {priorityItem.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Tooltip>
      <Tooltip title={generalInfoDisabledTooltipMsg} placement="rightBottom">
        <Form.Item label="Submittal Description">
          <TextArea
            disabled={
              isRevisionMode ||
              isIntegrationMode ||
              isSubmittalInDoneState ||
              cannotRespondToWorkflow ||
              !editableFields.includes("description")
            }
            value={localFeatureInstanceData?.description}
            rows={5}
            placeholder="Enter Description"
            onBlur={async (event: any) => {
              if (
                !checkValuesUpdated("description", event?.target?.value?.trim())
              )
                return;
              actions.updateFeatureInstanceFields({
                description: event.target.value?.trim()
              });
              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "description",
                  event?.target.value
                )
              );
            }}
            onChange={(event: any) => {
              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "description",
                  event.target.value
                )
              );
            }}
            data-testid="sgi-textarea-description"
          />
        </Form.Item>
      </Tooltip>
      {showSpecSectionFileModal && (
        <FileViewerModal
          fileKey={submittal?.snippet_file_key}
          pageNumber={submittal?.snippet_coordinates?.pageid || 0}
          showModal={showSpecSectionFileModal}
          hideModal={() => {
            setShowSpecSectionFileModal(false);
          }}
          headerComponent={specFileModalHeader()}
          data-testid="sgi-modal-spec-file"
        />
      )}
    </Form>
  );
}
